// This file can be replaced during build by using the `fileReplacements` array.
// `ng build --prod` replaces `environment.ts` with `environment.prod.ts`.
// The list of file replacements can be found in `angular.json`.

// Local dev ports, generated from .env by scripts/write-local-env.js (gitignored)
// so a git worktree can point at its own API. Staging/prod replace api.host.
import { LOCAL_API_PORT } from './local';

export const environment = {
  webAppUrl: 'http://localhost:4200',
  assetPath: '',
  production: false,
  aws: {
    region: 'ap-southeast-2',
  },
  forms: {
    dynamicRequiredClass: 'dynamic-required',
  },
  dbDateFormat: 'YYYY-MM-DD HH:mm:ss',
  // Sparse saves (#148 step 2).
  //   'off'    — always send the full form (legacy behaviour)
  //   'shadow' — send the full form PLUS the client diff so the server can
  //              compare the two and log fidelity; what gets saved is unchanged
  //   'on'     — send only the diff; the server applies exactly those paths
  // 'shadow'/'on' only activate against a server that advertises sparseSupported,
  // so this is safe to enable ahead of the server deploy.
  // Rollback = set back to 'shadow' and redeploy the client; no server change needed.
  //
  // INCIDENT 2026-07-13: sparse diffs omitted the __pathKey markers (constants
  // never differ from the snapshot), so every sparse-edited row was written
  // untagged and pathKey-dependent read models (dashboard tasks sync,
  // region/arranger filters, locations export) silently dropped those rows on
  // prod from 2026-07-10 ~14:51 AEST (v1.0.120). 'on' stays enabled here ONLY
  // because attachPathKeyMarkers (sparse-save.ts) now ships in the same release
  // and re-tags every changed path. Heal history with conductor-server
  // scripts/migrations 2026-07-13-1400-backfill-pathkeys-sparse-incident.sql,
  // and watch the rot-counter query in that file post-deploy (~0 = holding).
  // Rollback = 'shadow' + redeploy; no server change needed.
  sparseSaves: 'on' as 'off' | 'shadow' | 'on',
  // Account saves (#148 protocol, account flavour). 'on' sends only the changed
  // paths so concurrent edits to different records can't clobber each other;
  // 'shadow' sends the full payload + a logged dry-run diff; 'off' is legacy.
  accountSparseSaves: 'on' as 'off' | 'shadow' | 'on',
  // Service-provider saves, same protocol: 'on' sends only the changed paths
  // (two users editing different parts of the same provider can't clobber each
  // other); 'off' reverts to the legacy full-payload save.
  serviceProviderSparseSaves: 'on' as 'off' | 'on',
  arrangementTimeout: 1800000, // 1,800,000ms == 30mins
  status: {
    interval: 120000, // 120,000ms === 120sec
    awayTimeout: 600000, // 600,000ms === 10min
  },
  email: {
    sendAsync: true,
  },
  bdm: {
    nsw: {
      api: {
        username: '',
        password: '',
      },
      serviceProviderId: 65,
      productCategory: 'death-certificate',
      productName: 'Death Certificate',
    },
    qld: {
      api: {
        username: '',
        password: '',
      },
      serviceProviderId: 452,
      productCategory: 'death-certificate',
      productName: 'Death Certificate',
    },
    vic: {
      api: {
        username: 'P406_WS',
        password: '!KxJEJ6o8NLcQ!uko_t',
        clientId: '406',
      },
      serviceProviderId: 451,
      productCategory: 'death-certificate',
      productName: 'Death Certificate',
    },
  },
  api: {
    host: `http://${typeof window !== 'undefined' ? window.location.hostname : 'localhost'}:${LOCAL_API_PORT}/dev`,
    paths: {
      api: {
        auth: {
          login: '/api/auth/login',
          register: '/api/auth/register',
          forgottenPassword: '/api/auth/forgot-password',
          resetPassword: '/api/auth/reset-password',
          status: '/api/auth/status',
        },
        user: {
          isAuthenticated: '/api/user/is-authenticated',
          patchStatus: '/api/user/status',
          patchMessage: '/api/user/message',
          getMessages: '/api/user/messages',
          getUnreadMessageCount: '/api/user/messages/unread',
          getMessagesById: '/api/user/messages/:userId',
          patchMessageToRead: '/api/user/messages/:userId/set-to-read',
          getAll: '/api/user',
          getRegions: '/api/user/regions',
          getRoles: '/api/user/roles',
          updateUser: '/api/user/',
          profile: '/api/user/profile',
          // Website → Profiles (#184): edit ANOTHER user's public profile.
          // Distinct from `profile`, which is self-service and binds to the caller.
          profileForUser: '/api/user/:userId/profile',
          signaturePreview: '/api/user/email-signature/preview',
          uploadFile: '/api/upload/file',
          uploadAvatar: '/api/upload/avatar',
          changePassword: '/api/user/change-password',
          enable: '/api/user/enable/:userId',
          disable: '/api/user/disable/:userId',
          addBookmark: '/api/user/bookmarks/:arrangementId',
          removeBookmark: '/api/user/bookmarks/:arrangementId',
        },
        message: {
          direct: {
            patchMessage: '/api/message/direct',
            patchMessageToRead: '/api/message/direct/set-to-read',
            getMessages: '/api/message/direct',
          },
          groups: {
            patchMessage: '/api/message/sendMessage',
            getAll: '/api/groups',
            create: '/api/groups/create',
            getMessageById: '/api/groups/getMessages/:groupId',
          },
        },
        account: {
          get: '/api/account',
          getAll: '/api/account/all',
          getById: '/api/account/:accountId',
          post: '/api/account',
          patch: '/api/account',
        },
        agedCare: {
          enrolment: '/api/account/aged-care-enrolment',
        },
        // The location layer: provider blocks linked to crawl places (conductor-server #248).
        locations: {
          review: '/api/locations/review',
          service: '/api/locations/service/:serviceId',
          match: '/api/locations/match',
          block: '/api/locations/blocks/:serviceId/:metaId',
          // The arrangers' venue map (#250).
          nearby: '/api/locations/nearby',
          centre: '/api/locations/centre',
          suburbs: '/api/locations/suburbs',
          onboard: '/api/locations/onboard',
          onboardCheck: '/api/locations/onboard/check',
          // Curation and coverage for Operations (#251).
          curation: '/api/locations/curation',
          coverage: '/api/locations/coverage',
          // Places in the Suburbs tool (#253 step 3): /suburb/:salCode/places, /place/:locationId/…, /places/pending.
          base: '/api/locations',
          // The matching pipeline's decisions, for people to review (#261): /decisions, /sample, accept, undo.
          ai: '/api/locations/ai',
        },
        // Failure reports from the browser, one log line each (conductor-server #283).
        clientEvents: '/api/client-events',
        // Places we've used, from arrangements (conductor-server #249). Internal only.
        usage: {
          eventTypes: '/api/usage/event-types',
        },
        // Suburb map + suburb data (conductor-server #247, #256).
        suburbAreas: {
          list: '/api/website/suburb-areas',
          meta: '/api/website/suburb-areas/meta',
          patch: '/api/website/suburb-areas',
          undo: '/api/website/suburb-areas/undo',
          // Sub-regions (#260): one sub-region, the councils for "Fill from council areas", and the fill.
          subRegion: '/api/website/suburb-areas/subregions/:id',
          councilAreas: '/api/website/suburb-areas/council-areas',
          fill: '/api/website/suburb-areas/fill',
        },
        suburbData: {
          list: '/api/website/suburb-data',
          funnel: '/api/website/suburb-data/funnel',
          one: '/api/website/suburb-data/:salCode',
        },
        // #262: the readiness view (rows, owners, one page's blocks, owner and status), the page templates' seed, and
        // converting a page designed before the template.
        suburbReadiness: {
          list: '/api/website/suburb-readiness',
          owners: '/api/website/suburb-readiness/owners',
          one: '/api/website/suburb-readiness/:recordId',
          seedTemplates: '/api/website/page-templates/seed',
          convert: '/api/website/suburb-pages/convert',
        },
        // #263: one block (or a suburb page) drawn by picaluna-v3's own components, through the server's proxy.
        blockPreview: '/api/website/block-preview',
        picalunaSite: {
          publish: '/api/picaluna-site/publish',
          status: '/api/picaluna-site/status',
          promote: '/api/picaluna-site/promote',
          cancel: '/api/picaluna-site/cancel',
          stagedChanges: '/api/picaluna-site/staged-changes',
        },
        library: {
          folders: '/api/library/folders',
          folderTree: '/api/library/folders/tree',
          folderById: '/api/library/folders/:folderId',
          moveFolder: '/api/library/folders/:folderId/move',
          files: '/api/library/files',
          presignUpload: '/api/library/files/presign',
          fileById: '/api/library/files/:fileId',
          moveFile: '/api/library/files/:fileId/move',
          previewLink: '/api/library/files/:fileId/preview-link',
          downloadLink: '/api/library/files/:fileId/download-link',
          publishFile: '/api/library/files/:fileId/publish',
          unpublishFile: '/api/library/files/:fileId/unpublish',
        },
        files: {
          post: '/api/files',
          rename: '/api/files/rename/:fileId',
          getPreviewLink: '/api/files/get-preview-link/:fileId',
          getDownloadLink: '/api/files/get-download-link/:fileId',
          generatePresignedUploadUrl: '/api/files/generate-pre-signed-upload-url',
          saveUploadedFiles: '/api/files/save-uploaded-files',
          viewFile: '/api/files/view-file/:fileId',
        },
        arrangement: {
          get: '/api/arrangement',
          getById: '/api/arrangement/:arrangementId',
          touchById: '/api/arrangement/touch/:arrangementId',
          lockById: '/api/arrangement/lock/:arrangementId',
          unlockById: '/api/arrangement/unlock/:arrangementId',
          exportFull: '/api/arrangement/export/full',
          post: '/api/arrangement',
          patch: '/api/arrangement/:id',
          delete: '/api/arrangement/:id',
          addDoc: '/api/arrangement/add-document',
          updateDoc: '/api/arrangement/update-document',
          document: {
            create: '/api/arrangement/:arrangementId/documents',
            getAll: '/api/arrangement/:arrangementId/documents',
            deleteBatch: '/api/arrangement/:arrangementId/documents/batch',
          },
          contact: {
            post: '/api/arrangement/:id/contact',
            patch: '/api/arrangement/:id/contact',
          },
          bdm: {
            nsw: {
              lodge: '/api/arrangement/bdm/nsw/lodge',
            },
            vic: {
              lodge: '/api/arrangement/bdm/vic/lodge',
            },
            qld: {
              lodge: '/api/arrangement/bdm/qld/lodge',
            },
          },
          projectEstimate: {
            post: '/api/arrangement/:arrangementId/sync-project-tasks',
            reset: '/api/arrangement/:arrangementId/reset-project',
          },
          projectQuote: {
            post: '/api/arrangement/:arrangementId/generate-project-quote',
          },
          invoice: {
            post: '/api/arrangement/:arrangementId/invoice/',
          },
          simpleDataPdf: {
            post: '/api/arrangement/:arrangementId/generate-simple-data-pdf',
          },
          notifySendingInvoice: {
            post: '/api/arrangement/:arrangementId/notify-sending-invoice',
          },
          pdf: {
            post: '/api/arrangement/:arrangementId/pdf/:type',
          },
        },
        serviceProvider: {
          get: '/api/service',
          getById: '/api/service/:id',
          post: '/api/service',
          patch: '/api/service/:id',
          delete: '/api/service/:id',
          media: '/api/service/:id/media',
          typeMedia: '/api/service/:id/type/:typeKey/media',
          mediaDelete: '/api/service/media/batch',
          product: {
            patch: '/api/service/product/:productId',
            media: '/api/service/:id/product/:productSubKey/media',
          },
          products: {
            // DELETE with body { serviceMetaId, productIds[] } — batch soft-delete (#215)
            delete: '/api/service/:id/products',
          },
        },
        email: {
          send: '/api/email/send',
          get: '/api/email?toContactId=:toContactId&toServiceId=:toServiceId&arrangementId=:arrangementId',
          getById: '/api/email/:id?confirmationToken=:confirmationToken',
          read: '/api/email/:id/read?confirmationToken=:confirmationToken',
          accept: '/api/email/:id/accept?confirmationToken=:confirmationToken',
          reject: '/api/email/:id/reject?confirmationToken=:confirmationToken',
        },
        notification: {
          send: '/api/notification',
          read: '/api/notification/:id/read',
          archive: '/api/notification/:id/archive',
          getAll: '/api/notification',
          readAll: '/api/notification/read-all',
          archiveAll: '/api/notification/archive-all',
          // App releases (docs/RELEASE-NOTIFICATIONS-PLAN.md in conductor-server)
          appVersion: '/api/notification/app-version',
          releases: '/api/notification/releases',
          release: '/api/notification/releases/:id',
          sendRelease: '/api/notification/release',
          // Helper alerts — the `?` help icons
          helperAlerts: '/api/notification/helper-alerts',
          helperAlertsAll: '/api/notification/helper-alerts/all',
          helperAlert: '/api/notification/helper-alerts/:id',
        },
        invoice: {
          get: '/api/invoices',
          getStats: '/api/invoices/stats',
          getById: '/api/invoices/:id',
          updateDetails: '/api/invoices/:id/details',
          delete: '/api/invoices/:id',
          close: '/api/invoices/:id/close',
          post: '/api/invoices',
          postXeroInvoice: '/api/invoices/:id/xero-invoices',
          postNote: '/api/invoices/:id/notes',
          postDiscount: '/api/invoices/:id/discount',
          generatePdf: '/api/invoices/:id/generate-pdf',
        },
        reports: {
          get: '/api/reports',
        },
        payment: {
          post: '/api/invoices/:id/payments',
          getByNth: '/api/invoices/:invoiceId/payments/:nthPayment',
          confirm: '/api/invoices/:invoiceId/payments/:paymentId/confirm',
          reject: '/api/invoices/:invoiceId/payments/:paymentId/reject',
          updateTime: '/api/invoices/:invoiceId/payments/:paymentId/update-payment-time',
          delete: '/api/invoices/:invoiceId/payments/:paymentId',
          generatePdf: '/api/invoices/:invoiceId/payments/:paymentId/generate-pdf',
        },
      },
    },
  },
  appSync: {
    graphql: {
      host: '4bfnjgconvddrcgo5qq5wls4bu.appsync-api.ap-southeast-2.amazonaws.com',
      http: 'https://4bfnjgconvddrcgo5qq5wls4bu.appsync-api.ap-southeast-2.amazonaws.com/graphql',
      wss: 'wss://4bfnjgconvddrcgo5qq5wls4bu.appsync-realtime-api.ap-southeast-2.amazonaws.com/graphql',
    },
    apiKey: 'da2-l5gp42l67nedjifwaxikjpvfiu',
  },
  stripe: {
    // Khanh's local key. Use your own or from environment.staging.ts
    publicKey: 'pk_test_qtmA9X5hxYVezMD8YXTAcinD003nbIN9aR',
  },
};

/*
 * For easier debugging in development mode, you can import the following file
 * to ignore zone related error stack frames such as `zone.run`, `zoneDelegate.invokeTask`.
 *
 * This import should be commented out in production mode because it will have a negative impact
 * on performance if an error is thrown.
 */
// import 'zone.js/plugins/zone-error';  // Included with Angular CLI.
